import {
  decryptLocalStorageValue,
  encryptLocalStorageValue,
  hashLocalStorageKeyName,
  isEncryptedStorageValue,
  isHashedStorageKeyName,
  isMsalStorageKey,
  LocalStorageEncryptionKey,
  LocalStorageWritableValue,
  normalizeLocalStorageValue,
} from './local-storage-crypto.util';
import {
  getLocalStorageEncryptionKey,
  isLocalStorageEncryptionKeyReady,
  warnLocalStorageEncryption,
} from './local-storage-key-state.util';

export interface NativeBrowserStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
  key(index: number): string | null;
  readonly length: number;
}

type StorageKind = 'local' | 'session';

const readCache = new Map<string, string>();

function cacheId(kind: StorageKind, plainKey: string): string {
  return `${kind}:${plainKey}`;
}

export function clearEncryptedStorageReadCache(): void {
  readCache.clear();
}

function invalidateCache(kind: StorageKind, plainKey: string): void {
  readCache.delete(cacheId(kind, plainKey));
}

function safeEncrypt(
  plainText: LocalStorageWritableValue,
  encryptionKey: LocalStorageEncryptionKey
): string | null {
  try {
    return encryptLocalStorageValue(plainText, encryptionKey);
  } catch {
    return null;
  }
}

function safeDecrypt(
  cipherText: string,
  encryptionKey: LocalStorageEncryptionKey
): string | null {
  try {
    return decryptLocalStorageValue(cipherText, encryptionKey);
  } catch {
    return null;
  }
}

function resolveLegacyPlainValue(
  legacyValue: string,
  encryptionKey: LocalStorageEncryptionKey
): string {
  if (!isEncryptedStorageValue(legacyValue)) {
    return legacyValue;
  }

  const decrypted = safeDecrypt(legacyValue, encryptionKey);
  return decrypted ?? legacyValue;
}

function readEncryptedValue(
  native: NativeBrowserStorage,
  plainKey: string,
  encryptionKey: LocalStorageEncryptionKey
): string | null {
  const hashedKey = hashLocalStorageKeyName(plainKey, encryptionKey);
  const encrypted = native.getItem(hashedKey);

  if (encrypted !== null) {
    const decrypted = safeDecrypt(encrypted, encryptionKey);
    if (decrypted !== null) {
      return decrypted;
    }
  }

  const legacy = native.getItem(plainKey);
  if (legacy === null) {
    return null;
  }

  const plainValue = resolveLegacyPlainValue(legacy, encryptionKey);
  const encryptedLegacy = safeEncrypt(plainValue, encryptionKey);
  if (encryptedLegacy !== null) {
    native.setItem(hashedKey, encryptedLegacy);
    native.removeItem(plainKey);
  }

  return plainValue;
}

let pendingPlainKeyMigration = false;

export function schedulePlainStorageKeyMigration(): void {
  pendingPlainKeyMigration = true;
  if (isLocalStorageEncryptionKeyReady()) {
    flushPendingPlainStorageKeyMigration();
  }
}

export function flushPendingPlainStorageKeyMigration(
  nativeLocalStorage?: NativeBrowserStorage,
  nativeSessionStorage?: NativeBrowserStorage
): void {
  if (!pendingPlainKeyMigration || !isLocalStorageEncryptionKeyReady()) {
    return;
  }

  if (nativeLocalStorage && nativeSessionStorage) {
    migratePlainStorageKeys(nativeLocalStorage);
    migratePlainStorageKeys(nativeSessionStorage);
  }

  pendingPlainKeyMigration = false;
  clearEncryptedStorageReadCache();
}

export function migratePlainStorageKeys(native: NativeBrowserStorage): void {
  if (!isLocalStorageEncryptionKeyReady()) {
    return;
  }

  const encryptionKey = getLocalStorageEncryptionKey();
  if (!encryptionKey) {
    return;
  }

  const storedKeys: string[] = [];
  for (let index = 0; index < native.length; index += 1) {
    const key = native.key(index);
    if (key) {
      storedKeys.push(key);
    }
  }

  storedKeys.forEach(storedKey => {
    if (isMsalStorageKey(storedKey) || isHashedStorageKeyName(storedKey)) {
      return;
    }

    const legacyValue = native.getItem(storedKey);
    if (legacyValue === null) {
      return;
    }

    const plainValue = resolveLegacyPlainValue(legacyValue, encryptionKey);
    const hashedKey = hashLocalStorageKeyName(storedKey, encryptionKey);
    const encrypted = safeEncrypt(plainValue, encryptionKey);

    if (encrypted === null) {
      return;
    }

    if (native.getItem(hashedKey) === null) {
      native.setItem(hashedKey, encrypted);
    }

    native.removeItem(storedKey);
  });
}

export function migrateAllPlainBrowserStorageKeys(
  nativeLocalStorage: NativeBrowserStorage,
  nativeSessionStorage: NativeBrowserStorage
): void {
  migratePlainStorageKeys(nativeLocalStorage);
  migratePlainStorageKeys(nativeSessionStorage);
  pendingPlainKeyMigration = false;
  clearEncryptedStorageReadCache();
}

export function readEncryptedStorageItem(
  native: NativeBrowserStorage,
  kind: StorageKind,
  plainKey: string
): string | null {
  if (isMsalStorageKey(plainKey)) {
    return native.getItem(plainKey);
  }

  const cached = readCache.get(cacheId(kind, plainKey));
  if (cached !== undefined) {
    return cached;
  }

  if (!isLocalStorageEncryptionKeyReady()) {
    return native.getItem(plainKey);
  }

  const encryptionKey = getLocalStorageEncryptionKey();
  if (!encryptionKey) {
    return native.getItem(plainKey);
  }

  try {
    const value = readEncryptedValue(native, plainKey, encryptionKey);
    if (value !== null) {
      readCache.set(cacheId(kind, plainKey), value);
    }
    return value;
  } catch {
    return native.getItem(plainKey);
  }
}

let migrateAfterWriteScheduled = false;
let migrateAfterWriteTarget: NativeBrowserStorage | null = null;

function scheduleMigrateAfterWrite(native: NativeBrowserStorage): void {
  if (!isLocalStorageEncryptionKeyReady()) {
    return;
  }

  migrateAfterWriteTarget = native;
  if (migrateAfterWriteScheduled) {
    return;
  }

  migrateAfterWriteScheduled = true;
  queueMicrotask(() => {
    migrateAfterWriteScheduled = false;
    const target = migrateAfterWriteTarget;
    migrateAfterWriteTarget = null;
    if (target) {
      migratePlainStorageKeys(target);
      clearEncryptedStorageReadCache();
    }
  });
}

export function writeEncryptedStorageItem(
  native: NativeBrowserStorage,
  kind: StorageKind,
  plainKey: string,
  value: LocalStorageWritableValue
): void {
  const normalizedValue = normalizeLocalStorageValue(value);

  if (isMsalStorageKey(plainKey)) {
    native.setItem(plainKey, normalizedValue);
    return;
  }

  invalidateCache(kind, plainKey);

  if (!isLocalStorageEncryptionKeyReady()) {
    native.setItem(plainKey, normalizedValue);
    schedulePlainStorageKeyMigration();
    return;
  }

  const encryptionKey = getLocalStorageEncryptionKey();
  if (!encryptionKey) {
    native.setItem(plainKey, normalizedValue);
    return;
  }

  try {
    const hashedKey = hashLocalStorageKeyName(plainKey, encryptionKey);
    const encrypted = safeEncrypt(normalizedValue, encryptionKey);

    if (encrypted === null) {
      warnLocalStorageEncryption('localStorage encrypt failed; stored plain value', {
        plainKey,
      });
      native.setItem(plainKey, normalizedValue);
      return;
    }

    native.setItem(hashedKey, encrypted);
    native.removeItem(plainKey);
    scheduleMigrateAfterWrite(native);
  } catch (error) {
    warnLocalStorageEncryption('localStorage write encryption error; stored plain value', {
      plainKey,
      error: error instanceof Error ? error.message : String(error),
    });
    native.setItem(plainKey, normalizedValue);
    scheduleMigrateAfterWrite(native);
  }
}

export function removeEncryptedStorageItem(
  native: NativeBrowserStorage,
  kind: StorageKind,
  plainKey: string
): void {
  if (isMsalStorageKey(plainKey)) {
    native.removeItem(plainKey);
    return;
  }

  invalidateCache(kind, plainKey);

  if (isLocalStorageEncryptionKeyReady()) {
    const encryptionKey = getLocalStorageEncryptionKey();
    if (encryptionKey) {
      try {
        native.removeItem(hashLocalStorageKeyName(plainKey, encryptionKey));
      } catch {
        // Fall through to plain-key removal.
      }
    }
  }

  native.removeItem(plainKey);
}

export function clearEncryptedStorage(native: NativeBrowserStorage): void {
  clearEncryptedStorageReadCache();

  const keysToRemove: string[] = [];
  for (let index = 0; index < native.length; index += 1) {
    const key = native.key(index);
    if (key && !isMsalStorageKey(key)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => native.removeItem(key));
}
